import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MessageSquare, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { compactText, getStatusTone } from '@/lib/legalOs';
import { loadTodaySummary } from '@/lib/workflowData';
import { AppEmptyState, AppPage, AppPageHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';

export default function ClientLoopPage() {
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadTodaySummary({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setSummary(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Client Loop — Advoq';
    loadSummary();
  }, [loadSummary]);

  return (
    <main className="app-main" data-testid="client-loop-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Client Loop"
          title="Client-safe updates and follow-through"
          description="Outstanding requests, client-safe summaries, and delivery-ready communication surfaces."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadSummary} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
        />
        <WorkflowCompatibilityNotice message={loadNotice} />

        {!loading && !(summary?.client_followups || []).length ? (
          <AppEmptyState
            icon={MessageSquare}
            title="No client follow-ups are open"
            description="As matter ops collects requests and client-safe next steps, they will appear here."
          />
        ) : null}

        <div style={{ display: 'grid', gap: '14px' }}>
          {(summary?.client_followups || []).map((item, index) => {
            const tone = getStatusTone(item.status);
            return (
              <div key={`${item.id || item.title}_${index}`} className="advoq-card" style={{ padding: '16px', borderRadius: '22px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{item.title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>{compactText(item.description, 220)}</div>
                  </div>
                  <span className="app-page-chip" style={{ background: tone.background, color: tone.color, border: `1px solid ${tone.border}` }}>{item.status || 'pending'}</span>
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{item.matter_title || 'Matter not linked yet'}</div>
              </div>
            );
          })}
        </div>
      </AppPage>
    </main>
  );
}
